/* This example requires Tailwind CSS v2.0+ */
import React, { useEffect } from 'react';
import { useResetRecoilState, useSetRecoilState } from 'recoil';
import { Label } from '@nooz-optics/nooz-components-lib';
import { HeaderActions } from './Actions';
import { HeaderBanner } from './Banner/headerBanner';
import { DesktopHeaderNavigation } from './Navigation/Desktop';
import { IBlock } from '../../../../utils/types/contentful';
import { formatContentfulComponentProps } from '../../../../utils/globals';
import { MobileHeaderNavigation } from './Navigation/Mobile/mobileHeaderNavigation';
import { Link } from '../../Elements/Link';
import { SubHeaderState } from '../../../../utils/recoil/subHeader';
import { Slider, SliderArrow } from '../../Navigation/Slider/slider';
import { ZoneSwitcher } from '../../../Utils/ZoneSwitcher/zoneSwitcher';
import { Body, Button } from '../../Elements';
import { Image } from '../../Media';
import { useDrawer } from '../../../../utils/recoil/drawer';

export interface HeaderProps {
  navigations: IBlock;
  banner: IBlock;
  actions: IBlock;
  reassurances: IBlock;
}

const SubHeaderArrow = ({ placement, onClick }) => {
  const arrowButton = (
    <Button
      color={{ classname: 'primary' }}
      icon={{
        color: 'primary',
        iconKey: placement.includes('left')
          ? 'HiChevronLeft'
          : 'HiChevronRight',
      }}
    />
  );

  return (
    <SliderArrow
      onClick={onClick}
      button={arrowButton}
      placement={placement}
    />
  );
};

const PureHeader = ({
  navigations,
  banner,
  actions,
  reassurances,
  zoneSwitcher,
}: HeaderProps) => {
  const drawer = useDrawer();
  const setSubHeader = useSetRecoilState(SubHeaderState);
  const resetSubHeader = useResetRecoilState(SubHeaderState);

  const closeButton = <Button icon={{ iconKey: 'HiOutlineX', size: '2xl' }} />;

  const returnReassurances = () =>
    reassurances.items.map((reassurance) => {
      const { label, body, image } =
        formatContentfulComponentProps(reassurance);

      const handleOnClick = () => {
        drawer.open({
          placement: 'right',
          closeButton,
          title: null,
          actionButton: null,
          width: 'lg:w-screen/3 w-screen-95',
          body: (
            <div className='p-4'>
              <div className='relative flex flex-col h-auto'>
                <div className='h-1/2 overflow-hidden w-full'>
                  <Image
                    {...image}
                    objectFit='cover'
                    width='100%'
                  />
                </div>
                <div className='h-1/2 mt-4 flex flex-col justify-center text-left items-start'>
                  {body && <Body {...body} />}
                </div>
              </div>
            </div>
          ),
          footer: null,
          backdropLink: null,
        });
      };

      return (
        <button
          className='opacity-60 hover:opacity-100 flex w-full justify-center items-center cursor-pointer'
          onClick={handleOnClick}
          type='button'>
          <div className='w-full flex h-6 justify-center'>
            <Label
              {...label}
              truncate
              size='sm'
              icon={{ ...label.icon, size: 'lg' }}
            />
          </div>
        </button>
      );
    });

  useEffect(() => {
    if (reassurances) {
      setSubHeader({
        children: (
          <>
            <div className='hidden lg:w-2/3 xl:w-1/2 lg:flex justify-center px-10 py-2'>
              {returnReassurances()}
            </div>
            <div className='w-3/4 lg:hidden py-2 px-4 sm:px-6 '>
              <Slider
                centerPadding={0}
                centerMode
                hasAutoPlay
                isInfinite
                slidesToScroll={1}
                slidesToShow={1}
                autoplaySpeed={50000}
                hasArrows
                nextArrow={<SubHeaderArrow placement='right-0 -top-1' />}
                prevArrow={<SubHeaderArrow placement='left-0 -top-1' />}>
                {returnReassurances(reassurances)}
              </Slider>
            </div>
          </>
        ),
        isDefaultScroll: false,
        placement: 'top',
      });
    }
    return () => resetSubHeader();
  }, []);

  const handleOnNavigationHamburgerClick = () => {
    drawer.open({
      placement: 'left',
      closeButton,
      title: null,
      actionButton: null,
      width: 'lg:w-screen/2 w-screen-95',
      body: (
        <MobileHeaderNavigation
          navigations={navigations}
          actions={actions}
          banner={banner}
        />
      ),
      footer: null,
      backdropLink: null,
    });
  };

  return (
    <>
      <ZoneSwitcher {...formatContentfulComponentProps(zoneSwitcher)} />
      <HeaderBanner {...formatContentfulComponentProps(banner).list} />
      <div className='top-0 sticky w-full z-40 flex flex-col items-center border-b'>
        <div className='w-full mx-auto bg-white px-4 sm:px-6 lg:px-10'>
          <div className='flex justify-between h-16'>
            {/* Navigation hamburger, hidden in desktop */}
            <div className='flex w-1/4 items-center justify-start z-40 lg:hidden'>
              <Button
                onClick={handleOnNavigationHamburgerClick}
                icon={{ iconKey: 'HiMenu', size: '2xl' }}
              />
            </div>
            {/* Logo mobile center, left align desktop */}
            <div className='w-1/2 lg:w-1/4 flex items-center justify-center lg:justify-start'>
              <div className='flex items-center'>
                <Link
                  title='Go to Home page'
                  type='Page'
                  slug={{ url: '' }}
                  icon={{
                    iconKey: 'NoozLogo',
                    size: '7xl',
                    color: { hex: 'black' },
                  }}
                />
              </div>
            </div>
            {/*  Navigation desktop only */}
            <div className='hidden w-1/2 lg:flex items-center justify-center'>
              <DesktopHeaderNavigation navigations={navigations} />
            </div>
            {/* Right desktop element */}
            <div className='w-1/4 flex items-center justify-end'>
              <HeaderActions
                {...formatContentfulComponentProps(actions).list}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const Header = (props) => (
  <PureHeader {...formatContentfulComponentProps(props)} />
);
